import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarPlus, CheckCircle2, MapPin, Share2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, AGE_RANGES, fmtErr } from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";
import { fmtDate } from "@/components/UpcomingSessions";

export default function SessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const saved = JSON.parse(localStorage.getItem("c360_participant") || "null");
  const [form, setForm] = useState({
    name: saved?.name || "", email: saved?.email || "", phone: saved?.phone || "",
    age_range: saved?.age_range || "", experience: "Beginner", limitations: "", ack: false,
  });

  const load = () => api.get("/public/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const register = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post(`/public/sessions/${selected.id}/register`, {
        ...form, participant_id: saved?.participant_id,
        marketing_consent: saved ? undefined : false,
      });
      setDone(data);
      load();
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setLoading(false);
    }
  };

  const addToCalendar = () => {
    const s = done.session;
    const dt = s.date.replaceAll("-", "");
    const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "BEGIN:VEVENT",
      `UID:${done.registration.id}@capable360`, `DTSTART;VALUE=DATE:${dt}`,
      `SUMMARY:${s.name}`, `LOCATION:${s.location}`,
      `DESCRIPTION:${s.time} with ${s.trainer}. Come ready to move.`,
      "END:VEVENT", "END:VCALENDAR"].join("\r\n");
    const a = document.createElement("a");
    a.href = "data:text/calendar;charset=utf-8," + encodeURIComponent(ics);
    a.download = "capable360-session.ics";
    a.click();
  };

  return (
    <div className="min-h-screen bg-ink text-white">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="flex items-center justify-between px-5 sm:px-10 h-16 border-b border-zinc-800/70">
        <Link to="/" data-testid="sessions-home-link"><Wordmark className="text-2xl" /></Link>
        <Link to="/assessment" data-testid="sessions-assessment-link"
          className="text-[10px] uppercase tracking-[0.22em] font-bold text-volt hover:text-white transition-colors">
          Take the free assessment
        </Link>
      </header>

      <main className="px-5 sm:px-10 lg:px-16 py-14 sm:py-20 max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-volt transition-colors mb-8">
          <ArrowLeft size={14} /> Home
        </Link>
        <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-volt mb-4">Capable 360 Group Training</p>
        <h1 className="font-display uppercase text-5xl sm:text-7xl leading-[0.9]">Upcoming <span className="text-stroke">sessions.</span></h1>

        <div className="mt-12 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sessions.map((s, i) => (
            <motion.div key={s.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`session-card-${s.id}`}
              className="border border-zinc-800 bg-zinc-950 p-7 flex flex-col hover:border-volt/50 transition-colors">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.22em] text-volt font-bold">{fmtDate(s.date)}</p>
                <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{s.price}</span>
              </div>
              <h2 className="font-display uppercase text-3xl mt-2 leading-none">{s.name}</h2>
              <p className="text-zinc-300 text-sm mt-1">{s.time} · {s.duration}</p>
              <p className="mt-3 text-sm text-zinc-400 flex items-center gap-2"><MapPin size={14} className="text-zinc-600" /> {s.location}</p>
              <p className="mt-2 text-sm text-zinc-400">Focus: <span className="text-zinc-200">{s.focus}</span></p>
              <p className="text-xs text-zinc-500 mt-1">{s.difficulty} · Coach: {s.trainer}</p>
              {s.description && <p className="text-xs text-zinc-500 mt-3 leading-relaxed">{s.description}</p>}
              <div className="mt-6 pt-4 border-t border-zinc-800 flex items-center justify-between">
                <span className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-zinc-400">
                  <Users size={14} className="text-volt" />
                  <span data-testid={`session-spots-${s.id}`}>
                    {s.available > 0 ? `${s.available} of ${s.capacity} spots left` : `Full — waitlist open (${s.waitlisted})`}
                  </span>
                </span>
              </div>
              <button data-testid={`reserve-${s.id}`} onClick={() => { setSelected(s); setDone(null); setErr(""); }}
                className={`mt-5 uppercase tracking-[0.18em] text-xs font-extrabold py-4 transition-colors ${
                  s.available > 0 ? "bg-volt text-black hover:bg-white" : "border border-volt text-volt hover:bg-volt hover:text-black"}`}>
                {s.available > 0 ? "Reserve my spot" : "Join waitlist"}
              </button>
            </motion.div>
          ))}
        </div>
        {!sessions.length && <p className="text-zinc-500 mt-10">No upcoming sessions published yet — check back soon.</p>}
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}>
          <div data-testid="registration-modal" onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-zinc-950 border border-zinc-800 p-7 sm:p-9 max-h-[90vh] overflow-y-auto">
            {!done ? (
              <>
                <h2 className="font-display uppercase text-4xl leading-none">Reserve <span className="text-volt">your spot.</span></h2>
                <p className="text-sm text-zinc-400 mt-2">{fmtDate(selected.date)} · {selected.time} · {selected.location}</p>
                <form onSubmit={register} className="mt-7 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="reg-name" required placeholder="Full name *" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="col-span-2 sm:col-span-1 bg-ink border border-zinc-700 px-4 py-3.5 placeholder-zinc-600 focus:border-volt outline-none" />
                    <input data-testid="reg-email" required type="email" placeholder="Email *" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="col-span-2 sm:col-span-1 bg-ink border border-zinc-700 px-4 py-3.5 placeholder-zinc-600 focus:border-volt outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="reg-phone" placeholder="Phone (optional)" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="bg-ink border border-zinc-700 px-4 py-3.5 placeholder-zinc-600 focus:border-volt outline-none" />
                    <select data-testid="reg-age" required value={form.age_range}
                      onChange={(e) => setForm({ ...form, age_range: e.target.value })}
                      className="bg-ink border border-zinc-700 px-4 py-3.5 text-zinc-300 focus:border-volt outline-none">
                      <option value="" disabled>Age range *</option>
                      {AGE_RANGES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Experience level</p>
                    <div className="grid grid-cols-3 gap-2">
                      {["Beginner", "Intermediate", "Experienced"].map((x) => (
                        <button type="button" key={x} data-testid={`reg-exp-${x.toLowerCase()}`}
                          onClick={() => setForm({ ...form, experience: x })}
                          className={`border px-2 py-3 text-xs uppercase tracking-[0.12em] font-bold transition-colors ${form.experience === x ? "bg-volt text-black border-volt" : "border-zinc-700 text-zinc-300 hover:border-volt"}`}>
                          {x}
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea data-testid="reg-limitations" rows={3} value={form.limitations}
                    onChange={(e) => setForm({ ...form, limitations: e.target.value })}
                    placeholder="Any limitations or considerations your coach should know about? (optional)"
                    className="w-full bg-ink border border-zinc-700 px-4 py-3.5 placeholder-zinc-600 focus:border-volt outline-none" />
                  <label className="flex items-start gap-3 text-xs text-zinc-400 cursor-pointer">
                    <input type="checkbox" data-testid="reg-ack" required checked={form.ack}
                      onChange={(e) => setForm({ ...form, ack: e.target.checked })}
                      className="mt-0.5 accent-[#ff5c1a] w-4 h-4" />
                    I understand group training involves physical activity and I'll work within my own limits, flagging any discomfort to the coach.
                  </label>
                  {err && <p data-testid="reg-error" className="text-red-400 text-sm">{err}</p>}
                  <button type="submit" data-testid="reg-submit" disabled={loading}
                    className="w-full bg-volt text-black uppercase tracking-[0.18em] text-sm font-extrabold py-4 hover:bg-white transition-colors disabled:opacity-50">
                    {loading ? "Registering…" : "Register for session"}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center" data-testid="registration-confirmation">
                <CheckCircle2 size={48} className="text-volt mx-auto" />
                <h2 className="font-display uppercase text-5xl mt-4">
                  {done.registration.status === "confirmed" ? "You're in." : "You're on the waitlist."}
                </h2>
                <p className="text-sm text-zinc-300 mt-3">
                  {done.session.name} — {fmtDate(done.session.date)}, {done.session.time}
                </p>
                <p className="text-sm text-zinc-500">{done.session.location} · Come ready to move.</p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <button data-testid="reg-add-calendar" onClick={addToCalendar}
                    className="inline-flex items-center gap-2 bg-volt text-black uppercase tracking-[0.16em] text-xs font-extrabold px-6 py-4 hover:bg-white transition-colors">
                    <CalendarPlus size={16} /> Add to calendar
                  </button>
                  <button data-testid="reg-share" onClick={async () => {
                      await navigator.clipboard.writeText(`Join me at Capable 360 group training — ${window.location.origin}/sessions`);
                      toast.success("Link copied — send it to a friend.");
                    }}
                    className="inline-flex items-center gap-2 border border-zinc-600 uppercase tracking-[0.16em] text-xs font-bold px-6 py-4 hover:border-volt hover:text-volt transition-colors">
                    <Share2 size={16} /> Share with a friend
                  </button>
                </div>
                <button onClick={() => setSelected(null)} data-testid="reg-close"
                  className="mt-6 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-volt transition-colors">
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
